import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, Navigate } from 'react-router-dom';
import { FaArrowRight } from 'react-icons/fa';
import { useAuth } from '../lib/auth';

const KNOWN_ERRORS = {
  expired: "That sign-in link has expired or was already used. Enter your email below and we'll send a fresh one.",
  invalid: "We couldn't validate that sign-in link. Please request a new one.",
};

export default function Login() {
  const { user, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const authError = KNOWN_ERRORS[searchParams.get('auth')] || null;
  const next = searchParams.get('next') || '/my-courses';

  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(authError);

  useEffect(() => {
    document.title = 'Sign in — Learning Outcomes Academy';
    return () => { document.title = 'Learning Outcomes Academy'; };
  }, []);

  if (!loading && user) {
    return <Navigate to={next} replace />;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch('/api/auth/request', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed, next }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(json.error || 'Something went wrong. Please try again.');
        return;
      }
      setSent(true);
    } catch {
      setError('Unable to reach the server. Please check your connection.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="w-full bg-bone flex flex-col" style={{ minHeight: 'calc(100vh - 72px)' }}>
      <div className="container-page flex-1 flex items-center justify-center pt-4 pb-28">
        <div className="w-full max-w-md">
          <div className="text-[11px] tracking-[0.28em] uppercase text-brand-aubergine mb-4">
            Learning Outcomes Academy
          </div>

          {sent ? (
            <div>
              <h1 className="font-display text-4xl text-ink leading-tight mb-4">
                Check your inbox.
              </h1>
              <p className="text-slate2 text-base leading-relaxed mb-6">
                We sent a sign-in link to{' '}
                <span className="text-ink font-medium">{email}</span>. Click the
                link in that email to continue — it expires in 15 minutes.
              </p>
              <p className="text-sm text-slate2">
                Didn&rsquo;t get it?{' '}
                <button
                  type="button"
                  onClick={() => { setSent(false); setError(null); }}
                  className="underline underline-offset-2 hover:text-ink"
                >
                  Try again
                </button>{' '}
                or check your spam folder.
              </p>
            </div>
          ) : (
            <div>
              <h1 className="font-display text-4xl text-ink leading-tight mb-3">
                Sign in.
              </h1>
              <p className="text-slate2 text-base leading-relaxed mb-8">
                Enter your email and we&rsquo;ll send you a sign-in link. No
                password needed.
              </p>

              {error && (
                <div className="mb-6 text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3 leading-relaxed">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <label
                  htmlFor="login-email"
                  className="block text-[11px] tracking-[0.18em] uppercase text-brand-aubergine mb-2"
                >
                  Email address
                </label>
                <input
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  autoFocus
                  className="w-full rounded-xl border border-ink/15 bg-cream/60 px-4 py-4 text-lg text-ink focus:outline-none focus:border-brand-deep/60 focus:bg-white transition-colors mb-4"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Sending…' : (
                    <>Send my sign-in link <FaArrowRight className="text-[10px]" /></>
                  )}
                </button>
              </form>

              <p className="mt-8 text-xs text-slate2 leading-relaxed">
                New to Learning Outcomes Academy?{' '}
                <Link
                  to="/courses/ai-for-parents"
                  className="underline underline-offset-2 hover:text-ink"
                >
                  Browse the course
                </Link>{' '}
                — signing in automatically creates your account when you enrol.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
